import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { PaywallSheet } from "./PaywallSheet";

type PaywallContextValue = {
  open: (opts?: {
    feature?: string;
    variant?: "default" | "read_only";
  }) => void;
  close: () => void;
  isOpen: boolean;
};

const PaywallContext = createContext<PaywallContextValue | undefined>(
  undefined,
);

/**
 * Mount once at the app root. Any descendant can call `usePaywall().open()`
 * to surface the slide-up upsell sheet.
 */
export function PaywallProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [feature, setFeature] = useState<string | undefined>(undefined);
  const [variant, setVariant] = useState<"default" | "read_only">("default");

  const open = useCallback(
    (opts?: { feature?: string; variant?: "default" | "read_only" }) => {
      setFeature(opts?.feature);
      setVariant(opts?.variant ?? "default");
      setIsOpen(true);
    },
    [],
  );
  const close = useCallback(() => setIsOpen(false), []);

  const value = useMemo<PaywallContextValue>(
    () => ({ open, close, isOpen }),
    [open, close, isOpen],
  );

  return (
    <PaywallContext.Provider value={value}>
      {children}
      <PaywallSheet
        open={isOpen}
        onOpenChange={setIsOpen}
        feature={feature}
        variant={variant}
      />
    </PaywallContext.Provider>
  );
}

export function usePaywall(): PaywallContextValue {
  const ctx = useContext(PaywallContext);
  if (!ctx) throw new Error("usePaywall must be used inside <PaywallProvider>");
  return ctx;
}
